import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { ArrowLeft, Download, FileText, AlertCircle } from "lucide-react";
import api from "@/lib/api";

/**
 * Renders one legal document (AGB / Datenschutzerklärung / Impressum)
 * from the backend in Markdown. Public route, no auth required.
 * Lightweight Markdown renderer matching the subset used by the backend.
 */
const renderMarkdown = (md) => {
  if (!md) return null;
  const lines = md.split("\n");
  const blocks = [];
  const isSpecial = (l) => {
    const t = l.trim();
    return !t || t.startsWith("# ") || t.startsWith("## ") || t.startsWith("### ")
        || t.startsWith("- ") || t.startsWith("> ") || t.startsWith("|");
  };
  let i = 0;
  while (i < lines.length) {
    const line = lines[i];
    const trimmed = line.trim();
    if (!trimmed) { blocks.push({ type: "br" }); i++; continue; }
    if (trimmed.startsWith("# "))   { blocks.push({ type: "h1", text: trimmed.slice(2) });  i++; continue; }
    if (trimmed.startsWith("## "))  { blocks.push({ type: "h2", text: trimmed.slice(3) });  i++; continue; }
    if (trimmed.startsWith("### ")) { blocks.push({ type: "h3", text: trimmed.slice(4) });  i++; continue; }
    if (trimmed.startsWith("- ")) {
      const items = [];
      while (i < lines.length && lines[i].trimStart().startsWith("- ")) {
        let item = lines[i].trimStart().slice(2);
        i++;
        // Merge wrapped continuation lines into the current bullet
        while (i < lines.length && lines[i].trim() && !isSpecial(lines[i])) {
          item += " " + lines[i].trim();
          i++;
        }
        items.push(item);
      }
      blocks.push({ type: "ul", items });
      continue;
    }
    if (trimmed.startsWith("> ")) {
      const quote = [];
      while (i < lines.length && lines[i].trimStart().startsWith("> ")) {
        quote.push(lines[i].trimStart().slice(2));
        i++;
      }
      blocks.push({ type: "blockquote", lines: quote });
      continue;
    }
    if (trimmed.startsWith("|") && (i + 1) < lines.length && lines[i + 1].trim().startsWith("|")) {
      const rows = [];
      while (i < lines.length && lines[i].trim().startsWith("|")) {
        rows.push(lines[i].trim().replace(/^\||\|$/g, "").split("|").map(c => c.trim()));
        i++;
      }
      const data = rows.filter(r => !r.every(c => /^[-:]*$/.test(c)));
      blocks.push({ type: "table", rows: data });
      continue;
    }
    // Merge consecutive non-special lines into one paragraph
    const parts = [trimmed];
    i++;
    while (i < lines.length && lines[i].trim() && !isSpecial(lines[i])) {
      parts.push(lines[i].trim());
      i++;
    }
    blocks.push({ type: "p", text: parts.join(" ") });
  }

  // Inline replacement: **bold** + [text](url)
  const inline = (s) => {
    const out = [];
    let rest = s;
    let key = 0;
    while (rest.length) {
      const boldM = rest.match(/\*\*([^*]+)\*\*/);
      const linkM = rest.match(/\[([^\]]+)\]\(([^)]+)\)/);
      const next = [boldM, linkM].filter(Boolean).sort((a, b) => a.index - b.index)[0];
      if (!next) { out.push(rest); break; }
      if (next.index > 0) out.push(rest.slice(0, next.index));
      if (next === boldM) {
        out.push(<strong key={key++} className="font-semibold text-zinc-50">{boldM[1]}</strong>);
        rest = rest.slice(next.index + boldM[0].length);
      } else {
        out.push(
          <a key={key++} href={linkM[2]} target="_blank" rel="noopener noreferrer"
             className="text-[#00E575] underline hover:text-[#00C964]">{linkM[1]}</a>
        );
        rest = rest.slice(next.index + linkM[0].length);
      }
    }
    return out;
  };

  return blocks.map((b, k) => {
    switch (b.type) {
      case "h1": return <h1 key={k} className="text-3xl font-display font-bold text-zinc-50 mt-2 mb-4">{inline(b.text)}</h1>;
      case "h2": return <h2 key={k} className="text-xl font-display font-semibold text-zinc-100 mt-8 mb-2">{inline(b.text)}</h2>;
      case "h3": return <h3 key={k} className="text-base font-display font-semibold text-zinc-200 mt-5 mb-1">{inline(b.text)}</h3>;
      case "ul": return (
        <ul key={k} className="list-disc list-outside pl-6 space-y-1 text-zinc-300 mb-3">
          {b.items.map((it, j) => <li key={j} className="leading-relaxed">{inline(it)}</li>)}
        </ul>
      );
      case "blockquote": return (
        <blockquote key={k} className="border-l-2 border-[#00C964]/60 pl-4 my-3 text-zinc-400 italic">
          {b.lines.map((l, j) => <div key={j}>{inline(l)}</div>)}
        </blockquote>
      );
      case "table": return (
        <div key={k} className="my-4 overflow-x-auto">
          <table className="w-full text-sm border border-zinc-800">
            <thead className="bg-zinc-900"><tr>{b.rows[0].map((c, j) => <th key={j} className="px-3 py-2 text-left font-semibold text-zinc-100 border-b border-zinc-700">{inline(c)}</th>)}</tr></thead>
            <tbody>{b.rows.slice(1).map((row, r) => (
              <tr key={r} className="border-b border-zinc-800/60">{row.map((c, j) => <td key={j} className="px-3 py-2 text-zinc-300">{inline(c)}</td>)}</tr>
            ))}</tbody>
          </table>
        </div>
      );
      case "br": return <div key={k} className="h-2" />;
      case "p": default: return <p key={k} className="text-zinc-300 leading-relaxed mb-3">{inline(b.text)}</p>;
    }
  });
};

export default function LegalDocumentPage() {
  const { slug } = useParams();
  const [doc, setDoc] = useState(null);
  const [error, setError] = useState(null);

  useEffect(() => {
    api.get(`/legal/documents/${slug}`)
      .then(r => setDoc(r.data))
      .catch(() => setError("Dokument nicht gefunden"));
  }, [slug]);

  return (
    <div className="min-h-screen bg-[#09090B] text-zinc-100" data-testid={`legal-page-${slug}`}>
      <div className="max-w-3xl mx-auto px-5 py-8">
        <Link to="/login" className="inline-flex items-center gap-2 text-sm text-zinc-400 hover:text-zinc-200 mb-6" data-testid="legal-back-link">
          <ArrowLeft size={14} /> Zurück
        </Link>

        {error && (
          <div className="bg-rose-950/40 border border-rose-800 rounded-xl p-4 flex items-start gap-3 text-rose-100">
            <AlertCircle size={18} className="mt-0.5 shrink-0" />
            <span>{error}</span>
          </div>
        )}

        {doc && (
          <article className="bg-[#18181B] border border-zinc-800 rounded-2xl p-6 sm:p-10">
            <div className="flex flex-wrap items-center justify-between gap-3 mb-6 pb-4 border-b border-zinc-800">
              <div className="flex items-center gap-2 text-xs uppercase tracking-widest text-zinc-300">
                <FileText size={14} />
                <span>Stand {doc.effective_date} · v{doc.version}</span>
              </div>
              <a
                href={`${api.defaults.baseURL}/legal/documents/${slug}/pdf`}
                target="_blank" rel="noopener noreferrer"
                className="inline-flex items-center gap-2 px-3 py-1.5 rounded-lg bg-[#00C964] text-[#09090B] text-sm font-semibold hover:bg-[#00E575] transition"
                data-testid="legal-pdf-download-btn"
              >
                <Download size={14} /> Als PDF
              </a>
            </div>
            {renderMarkdown(doc.body_markdown)}
          </article>
        )}

        <div className="mt-6 text-center text-xs text-zinc-400 font-medium">
          <Link to="/legal/agb" className="hover:text-zinc-300">AGB</Link>
          <span className="mx-2">·</span>
          <Link to="/legal/datenschutz" className="hover:text-zinc-300">Datenschutz</Link>
          <span className="mx-2">·</span>
          <Link to="/legal/impressum" className="hover:text-zinc-300">Impressum</Link>
        </div>
      </div>
    </div>
  );
}
